import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import URLS from 'constants/urls';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import Segment from 'helpers/segment';

import { skuPayoutReportsMessages as messages } from './SkuPayoutReports.messages';

const FORMAT = 'csv';
const DEFAULT_FILE_NAME = `payout-report.${FORMAT}`;

const triggerBrowserDownload = (blob, fileName) => {
  if (process.env.NODE_ENV === 'test') return;
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, fileName);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  if (link?.parentNode?.removeChild) link.parentNode.removeChild(link);
};

const parseFilename = headers => {
  const header = headers?.['content-disposition'];
  if (!header) return DEFAULT_FILE_NAME;
  const match = header.split('filename=')[1];
  return match ? match.replace(/^"|"$/g, '') : DEFAULT_FILE_NAME;
};

const useSkuPayoutExport = ({ venueId, payoutId }) => {
  const dispatch = useDispatch();
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = useCallback(async () => {
    if (venueId == null || payoutId == null || isExporting) return;
    setIsExporting(true);
    try {
      const res = await httpClient.get(URLS.reports.payoutOrdersCsv({ payoutId, venueId }), {
        responseType: 'blob',
      });
      const fileName = parseFilename(res.headers);
      triggerBrowserDownload(new Blob([res.data]), fileName);
      dispatch(createSuccessSnackbar(messages.exportStarted));
      Segment.event('Payout Report Exported', { venueId, payoutId, format: FORMAT });
      braze.trackEvent(BRAZE_CUSTOM_EVENTS.PAYOUT_REPORT_EXPORTED);
    } catch (error) {
      reportError(error);
      dispatch(createErrorSnackbar(messages.exportFailed));
    } finally {
      setIsExporting(false);
    }
  }, [venueId, payoutId, isExporting, dispatch]);

  return { exportCsv, isExporting };
};

export default useSkuPayoutExport;
